import { trackDecisionTreeItemRefunded } from '../../analytics/searchEventAnalytics';
import * as toolSelectors from 'selectors/tools';
import * as extract from 'extractFromState';
import isEmpty from 'lodash/isEmpty';
import { getSimplrExpertPtrId } from '../../api/expert';
import { cancelRefund, refundShippedItems } from './CommonOrderActions';
import { formatOrderItemsToRefundAndCancel, formatOrderItemsToRefund } from 'util/decisionTreeUtil';
import { showErrorPopup } from 'components/popups/ErrorPopup/ErrorPopup';
import {
  cancelAndRefundOrderItems,
  cancelOrderItems,
  discountOrderItems,
  refundOrderItems,
} from './CommonAsyncOrderActions';
import { getOrderTags, getSelectedOrderId, getCustomerIdFromOrder } from 'selectors/tools';
import { extractSelectedTicketIdFromState } from 'extractFromState';
import { patchCustomerNotes, patchOrderTags } from '../../api/decisionTree';
import { handleError } from '../../services/ErrorService/ErrorService';

// called by cancelOrRefundOrder
const _cancelAndRefundItems = (orderingSystem, simplrAccountId, orderId, selectedShop, additionalOrderParams = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (!['shopify', 'revolve', 'common'].includes(orderingSystem)) {
      return showErrorPopup(`${orderingSystem} is not supported`, 'Error');
    }

    const cancelAndRefundItems = extract.getCancelAndRefundItems(state);
    if (isEmpty(cancelAndRefundItems)) {
      return;
    }

    const refundAndCancelItems = formatOrderItemsToRefundAndCancel(orderingSystem, cancelAndRefundItems);
    const userId = getSimplrExpertPtrId();

    if (orderingSystem === 'common') {
      // TODO this is temporary condition, till shopify gets refactored
      await dispatch(
        cancelAndRefundOrderItems({
          simplrAccountId,
          orderId,
          items: refundAndCancelItems,
          additionalOrderParams,
        })
      );
    } else {
      const actionReasons = extract.getActionReasons(state);
      const payload = {
        timeOut: 70000,
        userId,
        orderId: String(orderId),
        comment:
          actionReasons?.cancelOrRefundPrimaryReason || 'Order items canceled and refunded upon customer request',
        refundItems: refundAndCancelItems,
        shopName: selectedShop,
        addNotes: true,
      };

      await dispatch(cancelRefund(payload));
    }
  };
};

// called by cancelOrRefundOrder
const _onlyRefundItems = (orderingSystem, simplrAccountId, orderId, selectedShop, additionalOrderParams = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const refundOnlyItems = extract.getRefundOnlyItems(state);
    const conversationId = extractSelectedTicketIdFromState(state);

    if (isEmpty(refundOnlyItems)) {
      return;
    }

    if (!['shopify', 'common'].includes(orderingSystem)) {
      return showErrorPopup(`${orderingSystem} is not supported`, 'Error');
    }

    const refundItems = formatOrderItemsToRefund(orderingSystem, refundOnlyItems);

    const actionReasons = extract.getActionReasons(state);

    if (orderingSystem === 'common') {
      // TODO this is temporary condition, till shopify gets refactored
      await dispatch(refundOrderItems({ simplrAccountId, orderId, items: refundItems, additionalOrderParams }));
    } else {
      const payload = {
        refundItems,
        conversationId,
        addNotes: true,
        comment:
          actionReasons?.refundPrimaryReason ||
          actionReasons?.cancelOrRefundPrimaryReason ||
          'Order items refunded upon customer request',
        shopName: selectedShop,
        shopType: orderingSystem,
        simplrAccountId: simplrAccountId,
        orderId: String(orderId),
      };

      await dispatch(refundShippedItems(payload));
    }
  };
};

// called by cancelOrRefundOrder
const _onlyCancelItems = (orderingSystem, simplrAccountId, orderId) => {
  return async (dispatch, getState) => {
    const cancelOnlyItems = extract.getCancelOnlyItems(getState());

    if (isEmpty(cancelOnlyItems)) {
      return;
    }

    if (!['common'].includes(orderingSystem)) {
      return showErrorPopup(`${orderingSystem} is not supported`, 'Error');
    }
    const cancelItems = formatOrderItemsToRefund(orderingSystem, cancelOnlyItems);

    await dispatch(cancelOrderItems(simplrAccountId, orderId, cancelItems));
  };
};

const _onlyDiscountItems = (orderingSystem, simplrAccountId, orderId) => {
  return async (dispatch, getState) => {
    const discountOnlyItems = extract.getDiscountOnlyItems(getState());

    if (isEmpty(discountOnlyItems)) {
      return;
    }

    if (!['common'].includes(orderingSystem)) {
      return showErrorPopup(`${orderingSystem} is not supported`, 'Error');
    }
    const discountItems = formatOrderItemsToRefund(orderingSystem, discountOnlyItems);

    await dispatch(discountOrderItems({ simplrAccountId, orderId, items: discountItems }));
  };
};

// cancels and refunds unfulfilled items, refunds fulfilled items
export const cancelOrRefundOrder = (additionalOrderParams = {}) => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const orderingSystem = toolSelectors.getOrderingSystem(state);
      const simplrAccountId = extract.getSimplrAccountIdOfSelectedTicket(state);
      const orderId = getSelectedOrderId(state);
      const orderTags = getOrderTags(state);
      const customerId = getCustomerIdFromOrder(state);
      const selectedShop = toolSelectors.getSelectedShop(state);

      if (!['shopify', 'revolve', 'common'].includes(orderingSystem)) {
        return showErrorPopup(`${orderingSystem} is not supported`, 'Error');
      }

      await dispatch(
        _cancelAndRefundItems(orderingSystem, simplrAccountId, orderId, selectedShop, additionalOrderParams)
      );
      await dispatch(_onlyRefundItems(orderingSystem, simplrAccountId, orderId, selectedShop, additionalOrderParams));
      await dispatch(_onlyCancelItems(orderingSystem, simplrAccountId, orderId));

      if (!isEmpty(orderTags)) {
        const tags = orderTags.map((tag) => tag.value);
        const note = orderTags
          .map((tag) => tag.note)
          .filter((tagNote) => !!tagNote)
          .join(', ');

        await patchOrderTags({ orderId, tags, simplrAccountId, shopName: selectedShop, orderingSystem });
        if (note) {
          await patchCustomerNotes({
            simplrAccountId,
            note,
            customerId,
            shopName: selectedShop,
            orderingSystem,
          });
        }
      }

      trackDecisionTreeItemRefunded();
    } catch (error) {
      handleError({ error, showPopup: true });
    }
  };
};

export const cancelAndRefundOrDiscountOrder = (additionalOrderParams = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const orderingSystem = toolSelectors.getOrderingSystem(state);
    const simplrAccountId = extract.getSimplrAccountIdOfSelectedTicket(state);
    const orderId = getSelectedOrderId(state);

    const selectedShop = toolSelectors.getSelectedShop(state);

    if (!['shopify', 'revolve', 'common'].includes(orderingSystem)) {
      return showErrorPopup(`${orderingSystem} is not supported`, `Error`);
    }

    await dispatch(
      _cancelAndRefundItems(orderingSystem, simplrAccountId, orderId, selectedShop, additionalOrderParams)
    );
    await dispatch(_onlyRefundItems(orderingSystem, simplrAccountId, orderId, selectedShop, additionalOrderParams));
    await dispatch(_onlyCancelItems(orderingSystem, simplrAccountId, orderId));
    await dispatch(_onlyDiscountItems(orderingSystem, simplrAccountId, orderId));
    trackDecisionTreeItemRefunded();
  };
};
